import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUser,
  FaUsers,
  FaHome, 
  FaCamera, 
  FaMapMarkerAlt, 
  FaRupeeSign, 
  FaCheck,
  FaArrowLeft,
  FaArrowRight,
  FaUpload,
  FaTimes,
  FaPlus,
  FaMinus,
  FaWifi,
  FaCar,
  FaSwimmingPool,
  FaUtensils,
  FaTv,
  FaSnowflake,
  FaFire,
  FaPaw,
  FaSmoking,
  FaBan
} from 'react-icons/fa';
import './HostOnboarding.css';

const HostOnboarding = ({ onComplete, onClose, currentUser }) => {
  const isMountedRef = useRef(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    personalInfo: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      phoneNumber: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      emergencyContact: '',
      governmentId: '',
      governmentIdNumber: '',
      profilePhoto: null
    },
    // Property Basic Info
    propertyBasics: {
      propertyType: '',
      propertyName: '',
      propertyDescription: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      coordinates: { lat: null, lng: null }
    },
    // Property Details
    propertyDetails: {
      bedrooms: 1,
      bathrooms: 1,
      maxGuests: 2,
      propertySize: '',
      propertyAge: '',
      floorNumber: '',
      totalFloors: '',
      furnishing: '',
      availability: 'full-time'
    },
    // Amenities
    amenities: {
      essentials: [],
      features: [],
      safety: [],
      accessibility: []
    },
    // House Rules
    houseRules: {
      checkInTime: '15:00',
      checkOutTime: '11:00',
      smokingAllowed: false,
      petsAllowed: false,
      partiesAllowed: false,
      quietHours: { start: '22:00', end: '08:00' },
      additionalRules: ''
    },
    // Photos
    photos: {
      main: null,
      additional: []
    },
    // Pricing
    pricing: {
      basePrice: '',
      cleaningFee: '',
      securityDeposit: '',
      weeklyDiscount: 0,
      monthlyDiscount: 0,
      cancellationPolicy: 'moderate'
    }
  });

  const totalSteps = 2;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const amenitiesOptions = {
    essentials: [
      { id: 'wifi', label: 'WiFi', icon: FaWifi },
      { id: 'kitchen', label: 'Kitchen', icon: FaUtensils },
      { id: 'tv', label: 'TV', icon: FaTv },
      { id: 'ac', label: 'Air Conditioning', icon: FaSnowflake },
      { id: 'heating', label: 'Heating', icon: FaFire },
      { id: 'parking', label: 'Free Parking', icon: FaCar }
    ],
    features: [
      { id: 'pool', label: 'Swimming Pool', icon: FaSwimmingPool },
      { id: 'gym', label: 'Gym', icon: FaUser },
      { id: 'balcony', label: 'Balcony', icon: FaHome },
      { id: 'garden', label: 'Garden', icon: FaHome },
      { id: 'terrace', label: 'Terrace', icon: FaHome },
      { id: 'elevator', label: 'Elevator', icon: FaHome }
    ]
  };

  const propertyTypes = [
    { id: 'apartment', label: 'Apartment', description: 'A place that\'s part of a building' },
    { id: 'house', label: 'House', description: 'A standalone home' },
    { id: 'villa', label: 'Villa', description: 'A luxurious standalone home' },
    { id: 'studio', label: 'Studio', description: 'A single room with kitchen and bathroom' },
    { id: 'penthouse', label: 'Penthouse', description: 'A luxury apartment on the top floor' },
    { id: 'farmhouse', label: 'Farmhouse', description: 'A house in a rural setting' }
  ];

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateAmenities = (category, amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenities: {
        ...prev.amenities,
        [category]: prev.amenities[category].includes(amenityId)
          ? prev.amenities[category].filter(id => id !== amenityId)
          : [...prev.amenities[category], amenityId]
      }
    }));
  };

  const handleFileUpload = (file, type, index = null) => {
    if (type === 'profile') {
      updateFormData('personalInfo', 'profilePhoto', file);
    } else if (type === 'main') {
      updateFormData('photos', 'main', file);
    } else if (type === 'additional') {
      const newPhotos = [...formData.photos.additional];
      if (index !== null) {
        newPhotos[index] = file;
      } else {
        newPhotos.push(file);
      }
      updateFormData('photos', 'additional', newPhotos);
    }
  };

  const removePhoto = (type, index = null) => {
    if (type === 'main') {
      updateFormData('photos', 'main', null);
    } else if (type === 'additional') {
      const newPhotos = formData.photos.additional.filter((_, i) => i !== index);
      updateFormData('photos', 'additional', newPhotos);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleListingTypeSelect = (listingType) => {
    if (!isMountedRef.current) return;
    console.log('Host onboarding complete. Listing type:', listingType);
    onComplete({ ...formData, listingType });
  };

  const handleSaveStep1 = () => {
    if (!isMountedRef.current) return;
    onComplete({ ...formData, listingType: 'stay', fromStep1Save: true });
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
      return;
    }
    window.history.back();
  };

  const renderProgressBar = () => (
    <div className="progress-container">
      <div className="progress-bar">
        <div 
          className="progress-fill"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>
      <span className="progress-text">
        Step {currentStep} of {totalSteps}
      </span>
    </div>
  );

  const renderPersonalInfo = () => (
    <div className="onboarding-step">
      <div className="step-header">
        <h2>Welcome to UBSAAPP Host!</h2>
        <span>Universal Book&Stay Advisory App</span>
        <p>Let's get started by collecting some basic information about you</p>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label>First Name *</label>
          <input
            type="text"
            value={formData.personalInfo.firstName}
            onChange={(e) => updateFormData('personalInfo', 'firstName', e.target.value)}
            placeholder="Enter your first name"
            required
          />
        </div>

        <div className="form-group">
          <label>Last Name *</label>
          <input
            type="text"
            value={formData.personalInfo.lastName}
            onChange={(e) => updateFormData('personalInfo', 'lastName', e.target.value)}
            placeholder="Enter your last name"
            required
          />
        </div>

        <div className="form-group">
          <label>Date of Birth *</label>
          <input
            type="date"
            value={formData.personalInfo.dateOfBirth}
            onChange={(e) => updateFormData('personalInfo', 'dateOfBirth', e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>Phone Number *</label>
          <input
            type="tel"
            value={formData.personalInfo.phoneNumber}
            onChange={(e) => updateFormData('personalInfo', 'phoneNumber', e.target.value)}
            placeholder="+91 9876543210"
            required
          />
        </div>

        <div className="form-group full-width">
          <label>Address *</label>
          <input
            type="text"
            value={formData.personalInfo.address}
            onChange={(e) => updateFormData('personalInfo', 'address', e.target.value)}
            placeholder="Enter your complete address"
            required
          />
        </div>

        <div className="form-group">
          <label>City *</label>
          <input
            type="text"
            value={formData.personalInfo.city}
            onChange={(e) => updateFormData('personalInfo', 'city', e.target.value)}
            placeholder="Enter your city"
            required
          />
        </div>

        <div className="form-group">
          <label>State *</label>
          <input
            type="text"
            value={formData.personalInfo.state}
            onChange={(e) => updateFormData('personalInfo', 'state', e.target.value)}
            placeholder="Enter your state"
            required
          />
        </div>

        <div className="form-group">
          <label>PIN Code *</label>
          <input
            type="text"
            value={formData.personalInfo.pincode}
            onChange={(e) => updateFormData('personalInfo', 'pincode', e.target.value)}
            placeholder="123456"
            required
          />
        </div>

        <div className="form-group">
          <label>Emergency Contact</label>
          <input
            type="tel"
            value={formData.personalInfo.emergencyContact}
            onChange={(e) => updateFormData('personalInfo', 'emergencyContact', e.target.value)}
            placeholder="+91 9876543210"
          />
        </div>

        <div className="form-group">
          <label>Government ID Type *</label>
          <select
            value={formData.personalInfo.governmentId}
            onChange={(e) => updateFormData('personalInfo', 'governmentId', e.target.value)}
            required
          >
            <option value="">Select ID Type</option>
            <option value="aadhar">Aadhar Card</option>
            <option value="pan">PAN Card</option>
            <option value="passport">Passport</option>
            <option value="driving-license">Driving License</option>
            <option value="voter-id">Voter ID</option>
          </select>
        </div>

        <div className="form-group">
          <label>ID Number *</label>
          <input
            type="text"
            value={formData.personalInfo.governmentIdNumber}
            onChange={(e) => updateFormData('personalInfo', 'governmentIdNumber', e.target.value)}
            placeholder="Enter ID number"
            required
          />
        </div>

        <div className="form-group full-width">
          <label>Profile Photo</label>
          <div className="photo-upload-area">
            {formData.personalInfo.profilePhoto ? (
              <div className="uploaded-photo">
                <img 
                  src={URL.createObjectURL(formData.personalInfo.profilePhoto)} 
                  alt="Profile" 
                />
                <button 
                  type="button"
                  className="remove-photo"
                  onClick={() => updateFormData('personalInfo', 'profilePhoto', null)}
                >
                  <FaTimes />
                </button>
              </div>
            ) : (
              <label className="upload-label">
                <FaCamera />
                <span>Upload Profile Photo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files[0], 'profile')}
                  hidden
                />
              </label>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPropertyBasics = () => (
    <div className="onboarding-step">
      <div className="step-header">
        <h2>What type of property do you have?</h2>
        <p>Choose the option that best describes your space</p>
      </div>

      <div className="property-types-grid">
        {propertyTypes.map(type => (
          <div
            key={type.id}
            className={`property-type-card ${formData.propertyBasics.propertyType === type.id ? 'selected' : ''}`}
            onClick={() => updateFormData('propertyBasics', 'propertyType', type.id)}
          >
            <h3>{type.label}</h3>
            <p>{type.description}</p>
          </div>
        ))}
      </div>

      <div className="form-grid">
        <div className="form-group full-width">
          <label>Property Name *</label>
          <input
            type="text"
            value={formData.propertyBasics.propertyName}
            onChange={(e) => updateFormData('propertyBasics', 'propertyName', e.target.value)}
            placeholder="Give your property a catchy name"
            required
          />
        </div>

        <div className="form-group full-width">
          <label>Property Description *</label>
          <textarea
            value={formData.propertyBasics.propertyDescription}
            onChange={(e) => updateFormData('propertyBasics', 'propertyDescription', e.target.value)}
            placeholder="Describe your property, its unique features, and what makes it special..."
            rows={4}
            required
          />
        </div>

        <div className="form-group full-width">
          <label>Property Address *</label>
          <input
            type="text"
            value={formData.propertyBasics.address}
            onChange={(e) => updateFormData('propertyBasics', 'address', e.target.value)}
            placeholder="Enter complete property address"
            required
          />
        </div>

        <div className="form-group">
          <label>City *</label>
          <input
            type="text"
            value={formData.propertyBasics.city}
            onChange={(e) => updateFormData('propertyBasics', 'city', e.target.value)}
            placeholder="Property city"
            required
          />
        </div>

        <div className="form-group">
          <label>State *</label>
          <input
            type="text"
            value={formData.propertyBasics.state}
            onChange={(e) => updateFormData('propertyBasics', 'state', e.target.value)}
            placeholder="Property state"
            required
          />
        </div>

        <div className="form-group">
          <label>PIN Code *</label>
          <input
            type="text"
            value={formData.propertyBasics.pincode}
            onChange={(e) => updateFormData('propertyBasics', 'pincode', e.target.value)}
            placeholder="123456"
            required
          />
        </div>
      </div>
    </div>
  );

  const renderPropertyDetails = () => (
    <div className="onboarding-step">
      <div className="step-header">
        <h2>Property Details</h2>
        <p>Tell us more about your property specifications</p>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label>Number of Bedrooms *</label>
          <div className="counter-input">
            <button 
              type="button"
              onClick={() => updateFormData('propertyDetails', 'bedrooms', Math.max(0, formData.propertyDetails.bedrooms - 1))}
            >
              <FaMinus />
            </button>
            <span>{formData.propertyDetails.bedrooms}</span>
            <button 
              type="button"
              onClick={() => updateFormData('propertyDetails', 'bedrooms', formData.propertyDetails.bedrooms + 1)}
            >
              <FaPlus />
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>Number of Bathrooms *</label>
          <div className="counter-input">
            <button 
              type="button"
              onClick={() => updateFormData('propertyDetails', 'bathrooms', Math.max(1, formData.propertyDetails.bathrooms - 1))}
            >
              <FaMinus />
            </button>
            <span>{formData.propertyDetails.bathrooms}</span>
            <button 
              type="button"
              onClick={() => updateFormData('propertyDetails', 'bathrooms', formData.propertyDetails.bathrooms + 1)}
            >
              <FaPlus />
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>Maximum Guests *</label>
          <div className="counter-input">
            <button 
              type="button"
              onClick={() => updateFormData('propertyDetails', 'maxGuests', Math.max(1, formData.propertyDetails.maxGuests - 1))}
            >
              <FaMinus />
            </button>
            <span>{formData.propertyDetails.maxGuests}</span>
            <button 
              type="button"
              onClick={() => updateFormData('propertyDetails', 'maxGuests', formData.propertyDetails.maxGuests + 1)}
            >
              <FaPlus />
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>Property Size (sq ft)</label>
          <input
            type="number"
            value={formData.propertyDetails.propertySize}
            onChange={(e) => updateFormData('propertyDetails', 'propertySize', e.target.value)}
            placeholder="1200"
          />
        </div>

        <div className="form-group">
          <label>Property Age (years)</label>
          <input
            type="number"
            value={formData.propertyDetails.propertyAge}
            onChange={(e) => updateFormData('propertyDetails', 'propertyAge', e.target.value)}
            placeholder="5"
          />
        </div>

        <div className="form-group">
          <label>Floor Number</label>
          <input
            type="number"
            value={formData.propertyDetails.floorNumber}
            onChange={(e) => updateFormData('propertyDetails', 'floorNumber', e.target.value)}
            placeholder="2"
          />
        </div>

        <div className="form-group">
          <label>Total Floors</label>
          <input
            type="number"
            value={formData.propertyDetails.totalFloors}
            onChange={(e) => updateFormData('propertyDetails', 'totalFloors', e.target.value)}
            placeholder="4"
          />
        </div>

        <div className="form-group">
          <label>Furnishing Status *</label>
          <select
            value={formData.propertyDetails.furnishing}
            onChange={(e) => updateFormData('propertyDetails', 'furnishing', e.target.value)}
            required
          >
            <option value="">Select Furnishing</option>
            <option value="fully-furnished">Fully Furnished</option>
            <option value="semi-furnished">Semi Furnished</option>
            <option value="unfurnished">Unfurnished</option>
          </select>
        </div>

        <div className="form-group">
          <label>Availability *</label>
          <select
            value={formData.propertyDetails.availability}
            onChange={(e) => updateFormData('propertyDetails', 'availability', e.target.value)}
            required
          >
            <option value="full-time">Available Full Time</option>
            <option value="part-time">Available Part Time</option>
            <option value="weekends">Weekends Only</option>
            <option value="seasonal">Seasonal</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderAmenities = () => (
    <div className="onboarding-step">
      <div className="step-header">
        <h2>What amenities do you offer?</h2>
        <p>Select all the amenities available at your property</p>
      </div>

      <div className="amenities-section">
        <h3>Essential Amenities</h3>
        <div className="amenities-grid">
          {amenitiesOptions.essentials.map(amenity => {
            const Icon = amenity.icon;
            return (
              <div
                key={amenity.id}
                className={`amenity-card ${formData.amenities.essentials.includes(amenity.id) ? 'selected' : ''}`}
                onClick={() => updateAmenities('essentials', amenity.id)}
              >
                <Icon />
                <span>{amenity.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="amenities-section">
        <h3>Additional Features</h3>
        <div className="amenities-grid">
          {amenitiesOptions.features.map(amenity => {
            const Icon = amenity.icon;
            return (
              <div
                key={amenity.id}
                className={`amenity-card ${formData.amenities.features.includes(amenity.id) ? 'selected' : ''}`}
                onClick={() => updateAmenities('features', amenity.id)}
              >
                <Icon />
                <span>{amenity.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderHouseRules = () => (
    <div className="onboarding-step">
      <div className="step-header">
        <h2>Set your house rules</h2>
        <p>Establish guidelines to ensure a great experience for everyone</p>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label>Check-in Time *</label>
          <input
            type="time"
            value={formData.houseRules.checkInTime}
            onChange={(e) => updateFormData('houseRules', 'checkInTime', e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>Check-out Time *</label>
          <input
            type="time"
            value={formData.houseRules.checkOutTime}
            onChange={(e) => updateFormData('houseRules', 'checkOutTime', e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>Quiet Hours Start</label>
          <input
            type="time"
            value={formData.houseRules.quietHours.start}
            onChange={(e) => updateFormData('houseRules', 'quietHours', {
              ...formData.houseRules.quietHours,
              start: e.target.value
            })}
          />
        </div>

        <div className="form-group">
          <label>Quiet Hours End</label>
          <input
            type="time"
            value={formData.houseRules.quietHours.end}
            onChange={(e) => updateFormData('houseRules', 'quietHours', {
              ...formData.houseRules.quietHours,
              end: e.target.value
            })}
          />
        </div>
      </div>

      <div className="rules-toggles">
        <div className="rule-toggle">
          <div className="rule-info">
            <FaSmoking />
            <div>
              <h4>Smoking</h4>
              <p>Allow smoking inside the property</p>
            </div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={formData.houseRules.smokingAllowed}
              onChange={(e) => updateFormData('houseRules', 'smokingAllowed', e.target.checked)}
            />
            <span className="slider"></span>
          </label>
        </div>

        <div className="rule-toggle">
          <div className="rule-info">
            <FaPaw />
            <div>
              <h4>Pets</h4>
              <p>Allow pets in the property</p>
            </div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={formData.houseRules.petsAllowed}
              onChange={(e) => updateFormData('houseRules', 'petsAllowed', e.target.checked)}
            />
            <span className="slider"></span>
          </label>
        </div>

        <div className="rule-toggle">
          <div className="rule-info">
            <FaHome />
            <div>
              <h4>Parties & Events</h4>
              <p>Allow parties and events</p>
            </div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={formData.houseRules.partiesAllowed}
              onChange={(e) => updateFormData('houseRules', 'partiesAllowed', e.target.checked)}
            />
            <span className="slider"></span>
          </label>
        </div>
      </div>

      <div className="form-group full-width">
        <label>Additional Rules</label>
        <textarea
          value={formData.houseRules.additionalRules}
          onChange={(e) => updateFormData('houseRules', 'additionalRules', e.target.value)}
          placeholder="Any additional rules or guidelines for guests..."
          rows={4}
        />
      </div>
    </div>
  );

  const renderPhotos = () => (
    <div className="onboarding-step">
      <div className="step-header">
        <h2>Add photos of your property</h2>
        <p>Great photos help your listing stand out and attract more guests</p>
      </div>

      <div className="photos-section">
        <div className="main-photo-section">
          <h3>Main Photo *</h3>
          <p>This will be the first photo guests see</p>
          <div className="main-photo-upload">
            {formData.photos.main ? (
              <div className="uploaded-main-photo">
                <img 
                  src={URL.createObjectURL(formData.photos.main)} 
                  alt="Main property" 
                />
                <button 
                  type="button"
                  className="remove-photo"
                  onClick={() => removePhoto('main')}
                >
                  <FaTimes />
                </button>
              </div>
            ) : (
              <label className="main-upload-label">
                <FaCamera />
                <span>Upload Main Photo</span>
                <p>Recommended: 1200x800px or higher</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files[0], 'main')}
                  hidden
                />
              </label>
            )}
          </div>
        </div>

        <div className="additional-photos-section">
          <h3>Additional Photos</h3>
          <p>Add more photos to showcase different areas of your property</p>
          <div className="additional-photos-grid">
            {formData.photos.additional.map((photo, index) => (
              <div key={index} className="additional-photo">
                <img 
                  src={URL.createObjectURL(photo)} 
                  alt={`Property ${index + 1}`} 
                />
                <button 
                  type="button"
                  className="remove-photo"
                  onClick={() => removePhoto('additional', index)}
                >
                  <FaTimes />
                </button>
              </div>
            ))}
            {formData.photos.additional.length < 10 && (
              <label className="add-photo-label">
                <FaPlus />
                <span>Add Photo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files[0], 'additional')}
                  hidden
                />
              </label>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPricing = () => (
    <div className="onboarding-step">
      <div className="step-header">
        <h2>Set your pricing</h2>
        <p>You can always change your prices later</p>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label>Base Price per Night (₹) *</label>
          <input
            type="number"
            value={formData.pricing.basePrice}
            onChange={(e) => updateFormData('pricing', 'basePrice', e.target.value)}
            placeholder="2500"
            required
          />
        </div>

        <div className="form-group">
          <label>Cleaning Fee (₹)</label>
          <input
            type="number"
            value={formData.pricing.cleaningFee}
            onChange={(e) => updateFormData('pricing', 'cleaningFee', e.target.value)}
            placeholder="500"
          />
        </div>

        <div className="form-group">
          <label>Security Deposit (₹)</label>
          <input
            type="number"
            value={formData.pricing.securityDeposit}
            onChange={(e) => updateFormData('pricing', 'securityDeposit', e.target.value)}
            placeholder="2000"
          />
        </div>

        <div className="form-group">
          <label>Weekly Discount (%)</label>
          <input
            type="number"
            value={formData.pricing.weeklyDiscount}
            onChange={(e) => updateFormData('pricing', 'weeklyDiscount', e.target.value)}
            placeholder="10"
            min="0"
            max="50"
          />
        </div>

        <div className="form-group">
          <label>Monthly Discount (%)</label>
          <input
            type="number"
            value={formData.pricing.monthlyDiscount}
            onChange={(e) => updateFormData('pricing', 'monthlyDiscount', e.target.value)}
            placeholder="20"
            min="0"
            max="50"
          />
        </div>

        <div className="form-group">
          <label>Cancellation Policy *</label>
          <select
            value={formData.pricing.cancellationPolicy}
            onChange={(e) => updateFormData('pricing', 'cancellationPolicy', e.target.value)}
            required
          >
            <option value="flexible">Flexible - Full refund 1 day prior</option>
            <option value="moderate">Moderate - Full refund 5 days prior</option>
            <option value="strict">Strict - 50% refund up to 1 week prior</option>
          </select>
        </div>
      </div>

      <div className="pricing-summary">
        <h3>Pricing Summary</h3>
        <div className="summary-item">
          <span>Base price per night:</span>
          <span>₹{formData.pricing.basePrice || 0}</span>
        </div>
        {formData.pricing.cleaningFee && (
          <div className="summary-item">
            <span>Cleaning fee:</span>
            <span>₹{formData.pricing.cleaningFee}</span>
          </div>
        )}
        {formData.pricing.securityDeposit && (
          <div className="summary-item">
            <span>Security deposit:</span>
            <span>₹{formData.pricing.securityDeposit}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderListingTypeChoice = () => (
    <div className="onboarding-step">
      <div className="step-header step-header-with-close">
        {onClose && (
          <button
            type="button"
            className="onboarding-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        )}
        <h2>What would you like to list?</h2>
        <p>Choose one. You'll fill in the same details as when you add a property from your dashboard.</p>
      </div>
      <div className="listing-type-cards">
        <div
          className="listing-type-card stay-card"
          onClick={() => handleListingTypeSelect('stay')}
        >
          <div className="listing-card-icon">
            <FaHome />
          </div>
          <h3>Stay Property</h3>
          <p>Hotel, Villa, Homestay, Apartment, Resort — for overnight stays. You'll use the <strong>Add Property</strong> form with all details (location, rooms, check-in/out, pricing, amenities, photos, policies).</p>
          <span className="listing-card-tag">Same as Add Property</span>
        </div>
        <div
          className="listing-type-card meeting-card"
          onClick={() => handleListingTypeSelect('meeting')}
        >
          <div className="listing-card-icon">
            <FaUsers />
          </div>
          <h3>Meeting Room</h3>
          <p>Conference room, Board room, Office cabin — for meetings. You'll use the <strong>Add Meeting Room</strong> form with capacity, location, price per hour, availability, amenities.</p>
          <span className="listing-card-tag">Same as Add Meeting Room</span>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderPersonalInfo();
      case 2: return renderListingTypeChoice();
      default: return renderPersonalInfo();
    }
  };

  return (
    <div className="host-onboarding">
      <div className="onboarding-container">
        <button
          type="button"
          className="onboarding-top-back-btn"
          onClick={handleBack}
          aria-label="Go back"
        >
          <FaArrowLeft />
          <span>Back</span>
        </button>

        {renderProgressBar()}
        
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            style={{ minHeight: 0 }}
          >
            <div className="onboarding-step-content">
              {renderStepContent()}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="onboarding-navigation">
          {currentStep > 1 && (
            <button 
              type="button"
              className="btn-nav btn-prev"
              onClick={prevStep}
            >
              <FaArrowLeft />
              Previous
            </button>
          )}
          
          {currentStep === 1 && (
            <button 
              type="button"
              className="btn-nav btn-next"
              onClick={handleSaveStep1}
            >
              Save
              <FaCheck />
            </button>
          )}
          {currentStep === totalSteps && (
            <p className="onboarding-step-hint">Select Stay Property or Meeting Room above to continue with the same form as Add Property / Add Meeting Room.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HostOnboarding;
